import { MessagesSquare, Radio } from 'lucide-react'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge, Button, Card, CardContent, CardHeader } from '@/components/ui'

import {
  channelStatusBadgeVariant,
  channelStatusLabelKey,
  isChannelConnected,
} from '../channelStatus'
import type { ChannelSummary } from '../types'
import { ChannelReconnectPanel } from './ChannelReconnectPanel'

export interface ChannelCardProps {
  readonly tenantId: string
  readonly channel: ChannelSummary
}

/** WhatsApp e o unico canal provisionado hoje; outros tipos caem num icone generico neutro. */
function ChannelTypeIcon({ type }: { readonly type: string }) {
  return type === 'WhatsApp' ? (
    <MessagesSquare className="h-5 w-5" />
  ) : (
    <Radio className="h-5 w-5" />
  )
}

/**
 * Card de um canal: icone + nome, o status de conexao sempre visivel como Badge (Conectado=verde,
 * Aguardando/Desconectado=warning, Falhou=danger) e a data de criacao/atualizacao de forma amigavel.
 * A acao "Reconectar" abre, inline, o painel de QR reusado do onboarding. Quando o canal ja esta
 * conectado, a acao vira um discreto "Gerar novo codigo" (mesmo fluxo). Vocabulario neutro
 * (docs/design/00 s5): nunca cita provider/instancia/inbox.
 */
export function ChannelCard({ tenantId, channel }: ChannelCardProps) {
  const { t, i18n } = useTranslation()
  const [reconnecting, setReconnecting] = useState(false)
  const panelId = useId()

  const connected = isChannelConnected(channel.status)
  const typeLabel = channel.type === 'WhatsApp' ? t('channels.card.typeWhatsApp') : channel.type

  const formatDate = (value: string): string =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(value))

  const showUpdated = channel.updatedAt !== channel.createdAt

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-status-success-subtle text-status-success"
            >
              <ChannelTypeIcon type={channel.type} />
            </span>
            <div className="flex flex-col gap-0.5">
              <h3 className="text-base font-semibold text-foreground-primary">{channel.name}</h3>
              <span className="text-xs text-foreground-muted">{typeLabel}</span>
            </div>
          </div>

          <Badge variant={channelStatusBadgeVariant(channel.status)}>
            {t(channelStatusLabelKey(channel.status))}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col gap-0.5 text-xs text-foreground-muted">
            <span>{t('channels.card.createdLabel', { date: formatDate(channel.createdAt) })}</span>
            {showUpdated ? (
              <span>
                {t('channels.card.updatedLabel', { date: formatDate(channel.updatedAt) })}
              </span>
            ) : null}
          </div>

          {/* Reconectar so faz sentido quando o canal NAO esta conectado: gerar um QR para um canal ja
              conectado nao produz codigo novo (seria um no-op confuso). Um canal conectado apenas informa o status. */}
          {connected ? null : (
            <Button
              variant="primary"
              size="sm"
              aria-expanded={reconnecting}
              aria-controls={panelId}
              onClick={() => {
                setReconnecting((open) => !open)
              }}
            >
              {t('channels.card.reconnect')}
            </Button>
          )}
        </div>

        {connected ? (
          <p className="text-sm text-foreground-secondary">{t('channels.card.connectedHint')}</p>
        ) : null}

        {!connected && reconnecting ? (
          <div id={panelId}>
            <ChannelReconnectPanel
              tenantId={tenantId}
              channelId={channel.id}
              onClose={() => {
                setReconnecting(false)
              }}
            />
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
